import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { Icon } from '@helpers/icons';
import { Device, until } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';
export const CommonButtonTextStyles = css `
  ${transition('color', 0.25)};
  ${fonts.poppins.extraBold};

  position: relative;
  z-index: 2;
  color: currentColor;
  font-size: var(--fontSize);
  line-height: 1em;
  text-decoration: none;
`;
const Text = styled.span `
  ${CommonButtonTextStyles};

  [data-icon-only='true'] & {
    ${srOnly};
  }
`;
const IconWrapper = styled.div `
  ${transition('color', 0.25)};

  --iconHeight: 16px;

  position: relative;
  z-index: 2;
  display: grid;
  place-content: center;
  color: currentColor;
  height: var(--iconHeight);
  aspect-ratio: 1;
  margin: 0;
  padding: 0;

  > svg {
    display: block;
    height: var(--iconHeight);
  }

  [data-icon-position='left'] & {
    order: -1;
  }

  [data-icon-position='right'] & {
    order: 1;
  }

  [data-icon-type=${Icon.Arrow}] &,
  [data-icon-type=${Icon.ArrowThin}] &,
  [data-icon-type=${Icon.ChevronRight}] & {
    ${transition('transform, color', 0.25)};
  }

  [data-icon-type=${Icon.Arrow}][data-type='text'] &,
  [data-icon-type=${Icon.Arrow}][data-type='text-white'] &,
  [data-icon-type=${Icon.Arrow}][data-type='text-black'] & {
    ${transition('transform, color, opacity', 0.25)};

    opacity: 0;
  }

  [data-icon-type=${Icon.ChevronRight}] & {
    svg {
      transform: rotate(-90deg);
      width: var(--iconHeight);
    }
  }

  [data-icon-position='left'][data-icon-type=${Icon.Arrow}] &,
  [data-icon-position='left'][data-icon-type=${Icon.ArrowThin}] & {
    svg {
      transform: scale(-1);
    }
  }

  [data-icon-position='left'][data-icon-type=${Icon.ChevronRight}] & {
    svg {
      transform: rotate(-90deg) scale(-1);
    }
  }
`;
const IconHoverStyles = css `
  &[data-icon-type=${Icon.Arrow}],
  &[data-icon-type=${Icon.ArrowThin}],
  &[data-icon-type=${Icon.ChevronRight}] {
    ${IconWrapper} {
      transform: translateX(3px);
      opacity: 1;
    }
  }

  &[data-icon-position='left'][data-icon-type=${Icon.Arrow}],
  &[data-icon-position='left'][data-icon-type=${Icon.ArrowThin}],
  &[data-icon-position='left'][data-icon-type=${Icon.ChevronRight}] {
    ${IconWrapper} {
      transform: translateX(-3px);
    }
  }
`;
export const DefaultButtonStyles = css `
  ${ButtonReset};
  ${transition('background-color, color', 0.25)};

  --buttonHeight: ${fluid(40, 56)};
  --bgColour: transparent;
  --bgColourHover: transparent;
  --bgGradient: linear-gradient(
    90deg,
    ${brand.primary.orange} 0%,
    ${brand.secondary.orangeDark} 100%
  );
  --bgGradientHover: linear-gradient(0deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.4) 100%);
  --borderColour: transparent;
  --borderRadius: ${fluid(8, 12)};
  --borderWidth: ${fluid(2, 3)};
  --colourText: ${brand.primary.white};
  --colourTextDisabled: ${rgba(brand.primary.white, 0.8)};
  --colourTextHover: ${brand.primary.white};
  --fontSize: ${fluid(14, 18)};

  position: relative;
  display: inline-flex;
  gap: ${fluid(8, 12)};
  align-items: center;
  border: var(--borderWidth) solid var(--borderColour);
  background: none;
  background-image: var(--bgGradient);
  background-color: var(--bgColour);
  /* Compensate for transparent border when gradient bg is used and retained for border */
  background-size: calc(100% + ${fluid(4, 6)}) 100%;
  background-position: top center;
  color: var(--colourText);
  height: var(--buttonHeight);
  padding: 0 ${fluid(12, 16)};
  text-decoration: none;
  user-select: none;
  width: auto;
  border-radius: var(--borderRadius);

  /* Background hover for gradient to fade in */
  &::before {
    ${transition('opacity', 0.25)};

    content: '';
    position: absolute;
    height: 100%;
    width: 100%;
    background: var(--bgGradientHover);
    left: 0;
    top: 0;
    border-radius: calc(var(--borderRadius) * 0.85);
    opacity: 0;
  }

  /* Mobile click styles - button should mimic press */
  @media ${until(Device.TabletLarge)} {
    ${transition('background-color, color, transform', 0.15)};

    &:active {
      transform: translateY(1.5px);
    }
  }

  span& {
    .focus-ring & {
      outline: auto;
      outline-offset: 3px;
      outline-width: 2px;
    }

    .focus-ring &,
    button:hover &,
    a:hover & {
      ${IconHoverStyles};

      color: var(--colourTextHover);
      background-color: var(--bgColourHover);

      &::before {
        opacity: 1;
      }
    }
  }

  &:not(span) {
    cursor: pointer;

    &:focus-visible,
    &.focus-ring {
      outline: auto;
      outline-offset: 3px;
      outline-width: 2px;
    }

    &:focus-visible,
    &.focus-ring,
    &:hover {
      ${IconHoverStyles};

      color: var(--colourTextHover);
      background-color: var(--bgColourHover);

      &::before {
        opacity: 1;
      }
    }
  }

  /* Increase specificity to override :not(span) style */
  &:disabled:disabled:disabled {
    color: var(--colourTextDisabled);
    background-color: var(--bgColourHover);

    &::before {
      opacity: 1;
    }

    cursor: not-allowed;

    ${IconWrapper} {
      transform: none;
    }
  }
`;
export const OutlineButtonStyles = css `
  --bgOverlay: ${brand.primary.white};

  --borderColour: transparent;
  --colourText: ${brand.primary.black};

  /* Overlay to create gradient border effect */
  &::after {
    ${transition('opacity', 0.25)};

    content: '';
    box-shadow: 2px 1000px 1px var(--bgOverlay) inset;
    position: absolute;
    height: 100%;
    width: 100%;
    left: 0;
    top: 0;
    border-radius: ${fluid(6, 9)};
  }

  span& {
    .focus-ring &,
    button:hover &,
    a:hover & {
      &::after {
        opacity: 0;
      }
    }
  }

  &:not(span) {
    &.focus-ring,
    &:hover {
      &::after {
        opacity: 0;
      }
    }
  }

  &:disabled:disabled:disabled {
    &::after {
      opacity: 0;
    }
  }
`;
export const OutlineWhiteButtonStyles = css `
  --bgColour: transparent;
  --bgColourHover: ${brand.primary.white};
  --bgGradient: transparent;
  --bgGradientHover: transparent;
  --borderColour: ${brand.primary.white};
  --colourText: ${brand.primary.white};
  --colourTextDisabled: ${rgba(brand.primary.black, 0.66)};
  --colourTextHover: ${brand.primary.black};
`;
export const OutlineBlackButtonStyles = css `
  --bgColour: transparent;
  --bgColourHover: ${brand.primary.black};
  --bgGradient: transparent;
  --bgGradientHover: transparent;
  --borderColour: ${brand.primary.black};
  --colourText: ${brand.primary.black};
  --colourTextDisabled: ${rgba(brand.primary.white, 0.66)};
  --colourTextHover: ${brand.primary.white};
`;
export const SolidWhiteButtonStyles = css `
  --bgColour: ${brand.primary.white};
  --bgColourHover: transparent;
  --bgGradient: transparent;
  --bgGradientHover: transparent;
  --borderColour: ${brand.primary.white};
  --colourText: ${brand.primary.black};
  --colourTextDisabled: ${rgba(brand.primary.white, 0.8)};
  --colourTextHover: ${brand.primary.white};
`;
export const TextButtonStyles = css `
  --bgColour: transparent;
  --bgColourHover: ${brand.overlays.black};
  --bgGradient: transparent;
  --bgGradientHover: transparent;
  --borderColour: transparent;
  --colourText: ${brand.primary.black};
  --colourTextDisabled: ${rgba(brand.primary.black, 0.8)};
  --colourTextHover: ${brand.primary.black};

  ${Text} {
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 4px;
  }
`;
export const TextBlackButtonStyles = css `
  --bgColour: transparent;
  --bgColourHover: ${brand.overlays.white};
  --bgGradient: transparent;
  --bgGradientHover: transparent;
  --borderColour: transparent;
  --colourText: ${brand.primary.black};
  --colourTextDisabled: ${rgba(brand.primary.black, 0.8)};
  --colourTextHover: ${brand.primary.black};

  ${Text} {
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 4px;
  }
`;
export const TextWhiteButtonStyles = css `
  --bgColour: transparent;
  --bgColourHover: ${brand.overlays.white};
  --bgGradient: transparent;
  --bgGradientHover: transparent;
  --borderColour: transparent;
  --colourText: ${brand.primary.white};
  --colourTextDisabled: ${rgba(brand.primary.white, 0.8)};
  --colourTextHover: ${brand.primary.white};

  ${Text} {
    text-decoration: underline;
    text-decoration-thickness: 1px;
    text-underline-offset: 4px;
  }
`;
const Container = styled.button `
  ${DefaultButtonStyles};

  &[data-type='outline'] {
    ${OutlineButtonStyles};
  }

  &[data-type='outline-white'] {
    ${OutlineWhiteButtonStyles};
  }

  &[data-type='outline-black'] {
    ${OutlineBlackButtonStyles};
  }

  &[data-type='solid-white'] {
    ${SolidWhiteButtonStyles};
  }

  &[data-type='text'] {
    ${TextButtonStyles};
  }

  &[data-type='text-white'] {
    ${TextWhiteButtonStyles};
  }

  &[data-type='text-black'] {
    ${TextBlackButtonStyles};
  }

  &[data-icon-only='true'] {
    height: var(--buttonHeight);
    justify-content: center;
    padding: 0;
    width: var(--buttonHeight);
  }
`;
const ButtonStyles = {
    Container,
    IconWrapper,
    Text,
};
export default ButtonStyles;
